import React, { useState } from 'react';
import { TrendingUp, AlertTriangle, Flame, Calculator, Target, Zap } from 'lucide-react';

const DegenSystem = () => {
  const [selectedTier, setSelectedTier] = useState('safe');
  const [bankroll, setBankroll] = useState(1000);
  
  const calculateUnitSize = (tier) => {
    switch(tier) {
      case 'safe':
        return (bankroll / 50).toFixed(2);
      case 'risky':
        return (bankroll / 10).toFixed(2);
      case 'puredegen':
        return (bankroll * 0.5).toFixed(2) + ' - ' + bankroll.toFixed(2);
      default:
        return 0;
    }
  };

  const tiers = {
    safe: {
      name: 'Safe',
      icon: <TrendingUp className="text-green-500" size={40} />,
      color: 'green',
      description: 'Protect your bankroll while steadily growing it through disciplined, low-risk betting.'
    },
    risky: {
      name: 'Risky',
      icon: <AlertTriangle className="text-yellow-500" size={40} />,
      color: 'yellow',
      description: 'Take calculated risks with a larger unit size for bigger swings, while still maintaining some structure.'
    },
    puredegen: {
      name: 'PureDegen',
      icon: <Flame className="text-red-500" size={40} />,
      color: 'red',
      description: 'YOLO. Go big or go broke. This is the wildest tier, blending massive single bets with insane multis.'
    }
  };

  return (
    <div className="min-h-screen py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">The Degen System</h1>
          <p className="text-xl text-gray-600">
            A comprehensive bankroll management system for sports gambling with three tiers
          </p>
        </div>

        {/* Bankroll Calculator */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <Calculator className="text-blue-500 mr-3" size={24} />
            <h3 className="text-xl font-semibold">Unit Size Calculator</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Bankroll ($)
              </label>
              <input
                type="number"
                value={bankroll}
                onChange={(e) => setBankroll(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Unit Size
              </label>
              <div className="space-y-2">
                <div className="flex justify-between p-2 bg-green-50 rounded">
                  <span>Safe (1/50th):</span>
                  <span className="font-bold">${(bankroll / 50).toFixed(2)}</span>
                </div>
                <div className="flex justify-between p-2 bg-yellow-50 rounded">
                  <span>Risky (1/10th):</span>
                  <span className="font-bold">${(bankroll / 10).toFixed(2)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-50 rounded">
                  <span>PureDegen (50-100%):</span>
                  <span className="font-bold">${(bankroll * 0.5).toFixed(2)} - ${bankroll.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* General Rules */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">General Rules for All Tiers</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Initial Unit Size Calculation:</h3>
              <p className="text-gray-700">
                Your unit size starts at 1/50th of your initial bankroll (e.g., $1,000 bankroll = $20 unit).
                Unit size adjusts dynamically: Recalculate your unit size every time your bankroll increases or decreases by 10%.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Odds Definitions:</h3>
              <ul className="space-y-1 text-gray-700">
                <li>• <span className="font-medium">"Safe Bets":</span> Odds between 1.70 and 2.25</li>
                <li>• <span className="font-medium">"Moderate Bets":</span> Odds between 2.25 and 5.0</li>
                <li>• <span className="font-medium">"Long Shots":</span> Odds above 5.0</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Multi (Parlay) Strategy:</h3>
              <p className="text-gray-700">
                Multis can be used to combine speculative bets with a "safe bet" (1.70-1 to 2.25-1) to reduce the unit size needed for equivalent returns.
              </p>
            </div>
          </div>
        </div>

        {/* Tier Selector */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-lg shadow-sm" role="group">
            {Object.entries(tiers).map(([key, tier]) => (
              <button
                key={key}
                onClick={() => setSelectedTier(key)}
                className={`px-6 py-3 text-sm font-medium transition-all ${
                  selectedTier === key
                    ? key === 'safe' ? 'bg-green-500 text-white' :
                      key === 'risky' ? 'bg-yellow-500 text-white' :
                      'bg-red-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${
                  key === 'safe' ? 'rounded-l-lg' :
                  key === 'puredegen' ? 'rounded-r-lg' :
                  ''
                } border`}
              >
                {tier.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tier Details */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Tier 1: Safe */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${selectedTier === 'safe' ? 'ring-4 ring-green-400' : ''}`}>
            <div className="flex items-center mb-4">
              <TrendingUp className="text-green-500 mr-3" size={40} />
              <h3 className="text-2xl font-bold">Tier 1: Safe</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Protect your bankroll while steadily growing it through disciplined, low-risk betting.
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-semibold">Unit Size:</span> 1/50th of bankroll
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Betting Rules:</h4>
                <ul className="text-sm space-y-1">
                  <li>• <span className="font-medium">Safe Bets:</span> 0.5-1 unit</li>
                  <li>• <span className="font-medium">Moderate:</span> 0.25-0.5 unit</li>
                  <li>• <span className="font-medium">Long Shots:</span> 0.1 unit max</li>
                </ul>
              </div>
              <div className="bg-green-50 p-3 rounded text-sm">
                <span className="font-semibold">Example ($1,000):</span><br/>
                Unit = $20<br/>
                Safe Bet (2-1): $10-$20<br/>
                Moderate (4-1): $5-$10<br/>
                Long Shot (10-1): $2
              </div>
            </div>
          </div>

          {/* Tier 2: Risky */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${selectedTier === 'risky' ? 'ring-4 ring-yellow-400' : ''}`}>
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-yellow-500 mr-3" size={40} />
              <h3 className="text-2xl font-bold">Tier 2: Risky</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Take calculated risks with a larger unit size for bigger swings, while maintaining structure.
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-semibold">Unit Size:</span> 1/10th of bankroll (5x Safe)
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Betting Rules:</h4>
                <ul className="text-sm space-y-1">
                  <li>• <span className="font-medium">Safe Bets:</span> 0.5-1 unit</li>
                  <li>• <span className="font-medium">Moderate:</span> 0.25-0.5 unit</li>
                  <li>• <span className="font-medium">Long Shots:</span> 0.1 unit</li>
                </ul>
              </div>
              <div className="bg-yellow-50 p-3 rounded text-sm">
                <span className="font-semibold">Example ($1,000):</span><br/>
                Unit = $100<br/>
                Safe Bet (2-1): $50-$100<br/>
                Moderate (4-1): $25-$50<br/>
                Long Shot (10-1): $10
              </div>
            </div>
          </div>

          {/* Tier 3: PureDegen */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${selectedTier === 'puredegen' ? 'ring-4 ring-red-400' : ''}`}>
            <div className="flex items-center mb-4">
              <Flame className="text-red-500 mr-3" size={40} />
              <h3 className="text-2xl font-bold">Tier 3: PureDegen</h3>
            </div>
            <p className="text-gray-600 mb-4">
              YOLO. Go big or go broke. No rules, just vibes.
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-semibold">Unit Size:</span> 50%-100% of bankroll
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Betting Strategy:</h4>
                <p className="text-sm">
                  PSG shit in the UCL? Dump it all on Liverpool at 2.70. Want a 20-leg 20k-1 multi? Do it. Can't go wrong....Right?
                </p>
              </div>
              <div className="bg-red-50 p-3 rounded text-sm">
                <span className="font-semibold">Example ($1,000):</span><br/>
                All in baby! $500-$1,000 per bet<br/>
                20-leg parlays welcome<br/>
                Expected outcome: $10k or $0
              </div>
            </div>
          </div>
        </div>

        {/* Summary Table */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 overflow-x-auto">
          <h2 className="text-2xl font-bold mb-4">Summary Table</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Safe Bets (1.70-2.25)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moderate (2.25-5.0)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Long Shots (5.0+)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium">Safe</td>
                <td className="px-6 py-4 whitespace-nowrap">1/50th</td>
                <td className="px-6 py-4 whitespace-nowrap">0.5-1 unit</td>
                <td className="px-6 py-4 whitespace-nowrap">0.25-0.5 unit</td>
                <td className="px-6 py-4 whitespace-nowrap">0.1 unit</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium">Risky</td>
                <td className="px-6 py-4 whitespace-nowrap">1/10th</td>
                <td className="px-6 py-4 whitespace-nowrap">0.5-1 unit</td>
                <td className="px-6 py-4 whitespace-nowrap">0.25-0.5 unit</td>
                <td className="px-6 py-4 whitespace-nowrap">0.1 unit</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium">PureDegen</td>
                <td className="px-6 py-4 whitespace-nowrap">50%-100%</td>
                <td className="px-6 py-4 whitespace-nowrap" colSpan="3" className="text-center">
                  No rules - YOLO everything
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Practical Tips */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <Target className="mr-3 text-blue-500" />
            Practical Tips
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2 text-green-600">Safe Tier</h3>
              <p className="text-gray-700">
                Ideal for beginners or those with a small bankroll who want slow, steady growth.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-yellow-600">Risky Tier</h3>
              <p className="text-gray-700">
                Suits experienced bettors comfortable with volatility but not ready to go full degen.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-red-600">PureDegen Tier</h3>
              <p className="text-gray-700">
                For thrill-seekers who don't mind losing it all for a shot at massive wins. Expect wild swings—$1,000 could become $10,000 or $0 in a day.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-blue-600">Tracking</h3>
              <p className="text-gray-700">
                Keep a spreadsheet of your bankroll, bets, and unit size adjustments to stay disciplined (or at least semi-disciplined for PureDegen).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DegenSystem;